import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const Calendar = ({
  events: initialEvents = {},
  initialDate,
  onDateSelect,
  onEventSelect,
  onEventAdd,
  onEventDelete,
  onEventsChange,
}) => {
  // State initialization - combined related states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState({});
  const [newEvent, setNewEvent] = useState({
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    description: "",
  });
  const [uiState, setUiState] = useState({
    showAddForm: false,
    showDeleteForm: false,
    showAddConfirmation: false,
    showDeleteConfirmation: false,
  });
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Helper functions - combined formatting functions
  const formatters = {
    dateForInput: (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    dateKey: (date) =>
      `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`,
    dateFromString: (dateString) => {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    dateForDisplay: (date) =>
      new Date(date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    monthYear: (date) =>
      date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
  };

  // Initialize component with props
  useEffect(() => {
    if (initialEvents) setEvents(initialEvents);
    if (initialDate) {
      const dateObj = new Date(initialDate);
      setCurrentMonth(dateObj);
      setSelectedDate(dateObj);
      setNewEvent((prev) => ({
        ...prev,
        startDate: formatters.dateForInput(dateObj),
        endDate: formatters.dateForInput(dateObj),
      }));
    }
  }, [initialDate, initialEvents]);

  // Call the onEventsChange callback whenever events change
  useEffect(() => {
    if (onEventsChange) onEventsChange(events);
  }, [events, onEventsChange]);

  // Get days in month including padding days
  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysArray = [];

    // Add previous month's days
    for (let i = 0; i < firstDayOfMonth; i++) {
      const prevMonthDay = new Date(year, month, 0 - i).getDate();
      daysArray.unshift(new Date(year, month - 1, prevMonthDay));
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(new Date(year, month, i));
    }

    // Add next month's days to fill the calendar (6 rows x 7 columns = 42 cells)
    const remainingCells = 42 - daysArray.length;
    for (let i = 1; i <= remainingCells; i++) {
      daysArray.push(new Date(year, month + 1, i));
    }

    return daysArray;
  }, []);

  // UI state management - consolidated UI state updates
  const updateUiState = useCallback((updates) => {
    setUiState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Date and event handlers
  const handleDateClick = useCallback(
    (date) => {
      setSelectedDate(date);
      updateUiState({
        showAddForm: true,
        showDeleteForm: false,
        showAddConfirmation: false,
        showDeleteConfirmation: false,
      });
      setNewEvent((prev) => ({
        ...prev,
        startDate: formatters.dateForInput(date),
        endDate: formatters.dateForInput(date),
      }));
      if (onDateSelect) onDateSelect(date);
    },
    [formatters, onDateSelect, updateUiState]
  );

  const handleEventClick = useCallback(
    (date, eventDescription, eventData, e) => {
      e.stopPropagation();
      setSelectedDate(date);
      setSelectedEvent({ ...eventData, description: eventDescription });
      updateUiState({
        showDeleteForm: true,
        showAddForm: false,
        showAddConfirmation: false,
        showDeleteConfirmation: false,
      });
      if (onEventSelect) onEventSelect(date, eventDescription, eventData);
    },
    [onEventSelect, updateUiState]
  );

  const handleEventInputChange = useCallback((field, value) => {
    setNewEvent((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    updateUiState({
      showAddForm: false,
      showDeleteForm: false,
      showAddConfirmation: false,
      showDeleteConfirmation: false,
    });
    setSelectedEvent(null);
  }, [updateUiState]);

  const handleCancelConfirmation = useCallback(() => {
    updateUiState({
      showAddConfirmation: false,
      showDeleteConfirmation: false,
    });
  }, [updateUiState]);

  const showAddConfirmationDialog = useCallback(() => {
    const { description, startDate } = newEvent;
    if (!description || !startDate) return;
    updateUiState({ showAddConfirmation: true });
  }, [newEvent, updateUiState]);

  const showDeleteConfirmationDialog = useCallback(
    (e) => {
      e.preventDefault();
      updateUiState({ showDeleteConfirmation: true });
    },
    [updateUiState]
  );

  // Event management functions
  const handleAddEvent = useCallback(() => {
    const { startDate, endDate, startTime, endTime, description } = newEvent;
    if (!description || !startDate) return;

    const dateKey = formatters.dateKey(selectedDate);
    const newEventData = {
      id: new Date().getTime(),
      description,
      startDate,
      endDate: endDate || startDate,
      startTime: startTime || "00:00",
      endTime: endTime || "23:59",
    };

    setEvents((prevEvents) => {
      const updatedEvents = { ...prevEvents };
      if (!updatedEvents[dateKey]) updatedEvents[dateKey] = [];
      updatedEvents[dateKey] = [...updatedEvents[dateKey], newEventData];
      return updatedEvents;
    });

    updateUiState({ showAddForm: false, showAddConfirmation: false });
    setNewEvent((prev) => ({
      ...prev,
      description: "",
      startTime: "",
      endTime: "",
    }));

    if (onEventAdd) onEventAdd(selectedDate, newEventData);
  }, [newEvent, selectedDate, formatters, onEventAdd, updateUiState]);

  const handleDeleteEvent = useCallback(() => {
    if (!selectedEvent || !selectedDate) return;
    const dateKey = formatters.dateKey(selectedDate);

    setEvents((prevEvents) => {
      const updatedEvents = { ...prevEvents };
      if (updatedEvents[dateKey]) {
        updatedEvents[dateKey] = updatedEvents[dateKey].filter(
          (event) => event.id !== selectedEvent.id
        );
        if (updatedEvents[dateKey].length === 0) delete updatedEvents[dateKey];
      }
      return updatedEvents;
    });

    updateUiState({ showDeleteForm: false, showDeleteConfirmation: false });
    setSelectedEvent(null);

    if (onEventDelete) onEventDelete(selectedEvent.id);
  }, [selectedEvent, selectedDate, formatters, onEventDelete, updateUiState]);

  // Calendar helper functions
  const getDayEvents = useCallback(
    (date) => {
      const dateKey = formatters.dateKey(date);
      return events[dateKey] || [];
    },
    [events, formatters]
  );

  const getTodayEventsFromDatabase = useCallback(() => {
    const today = new Date();
    const formattedToday = formatters.dateForInput(today);
    const todayEvents = [];

    Object.keys(events).forEach((dateKey) => {
      const eventsForDate = events[dateKey];
      if (Array.isArray(eventsForDate)) {
        eventsForDate.forEach((event) => {
          if (
            event.startDate === formattedToday ||
            (typeof event.startDate === "string" &&
              event.startDate.includes(formattedToday))
          ) {
            todayEvents.push(event);
          }
        });
      }
    });

    return todayEvents;
  }, [events, formatters]);

  const isDateInEventRange = useCallback(
    (date, event) => {
      const eventStartDate = formatters.dateFromString(event.startDate);
      const eventEndDate = formatters.dateFromString(event.endDate);

      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      eventStartDate.setHours(0, 0, 0, 0);
      eventEndDate.setHours(0, 0, 0, 0);

      return compareDate >= eventStartDate && compareDate <= eventEndDate;
    },
    [formatters]
  );

  const getMultiDayEvents = useCallback(
    (date) => {
      const multiDayEvents = [];

      Object.keys(events).forEach((dateKey) => {
        events[dateKey].forEach((event) => {
          if (
            event.startDate !== event.endDate &&
            isDateInEventRange(date, event)
          ) {
            const isStartDate =
              formatters.dateKey(formatters.dateFromString(event.startDate)) ===
              formatters.dateKey(date);
            const startDate = formatters.dateFromString(event.startDate);
            const endDate = formatters.dateFromString(event.endDate);
            const daysDiff = Math.round(
              (endDate - startDate) / (1000 * 60 * 60 * 24)
            );

            multiDayEvents.push({ ...event, isStartDate, daysDiff });
          }
        });
      });

      return multiDayEvents;
    },
    [events, isDateInEventRange, formatters]
  );

  // Date validation helpers
  const dateValidators = {
    isToday: useCallback((date) => {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    }, []),
    isSelected: useCallback(
      (date) => {
        return (
          selectedDate &&
          date.getDate() === selectedDate.getDate() &&
          date.getMonth() === selectedDate.getMonth() &&
          date.getFullYear() === selectedDate.getFullYear()
        );
      },
      [selectedDate]
    ),
    isCurrentMonth: useCallback(
      (date) => date.getMonth() === currentMonth.getMonth(),
      [currentMonth]
    ),
  };

  const [validationError, setValidationError] = useState(false);

  const validateAndConfirm = () => {
    if (
      !newEvent.startDate ||
      !newEvent.endDate ||
      !newEvent.startTime ||
      !newEvent.endTime ||
      !newEvent.description
    ) {
      setValidationError(true);

      setTimeout(() => {
        setValidationError(false);
      }, 2000);

      document
        .querySelector(".event-form")
        .scrollIntoView({ behavior: "smooth" });
    } else {
      setValidationError(false);
      showAddConfirmationDialog();
    }
  };

  const days = getDaysInMonth(currentMonth);
  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const today = new Date();
  const formattedToday = formatters.dateForDisplay(today);
  const formattedSelectedDate = selectedDate
    ? formatters.dateForDisplay(selectedDate)
    : "";
  const todayEvents = getTodayEventsFromDatabase();
  const {
    showAddForm,
    showDeleteForm,
    showAddConfirmation,
    showDeleteConfirmation,
  } = uiState;

  return (
    <div className="container-fluid p-0 mt-4">
      <div className="d-flex flex-row gap-2">
        {/* Left sidebar */}
        <div className="col-md-3">
          <div className="card mb-2">
            <div className="card-body">
              <h6 className="card-title">Today</h6>
              <h5
                className="card-subtitle mb-2 mt-1"
                style={{ color: "#D18237" }}
              >
                {formattedToday}
              </h5>
              <div
                className="today-events text-muted text-center mt-4"
                style={{ fontSize: "12px" }}
              >
                {todayEvents.length > 0 ? (
                  todayEvents.map((event, index) => (
                    <div key={index} className="event-item">
                      <strong style={{ color: "black" }}>
                        {event.description}
                      </strong>
                      <div className="event-time">
                        {event.startTime} - {event.endTime}
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No events</p>
                )}
              </div>
            </div>
          </div>

          {/* Add Event Form */}
          {showAddForm && selectedDate && (
            <div
              className="card event-form"
              style={{
                maxWidth: "100%",
                margin: "0 auto",
                backgroundColor: "#FFFFFF",
              }}
            >
              <div className="card-body p-3">
                {/* Show validation warning if needed */}
                {validationError && (
                  <div
                    className="alert alert-danger mb-3 fade show"
                    role="alert"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Please fill in all required fields before saving.
                      </div>
                      <div className="alert-progress">
                        <div
                          className="alert-progress-bar"
                          style={{
                            width: "100%",
                            height: "3px",
                            backgroundColor: "#dc3545",
                            animation: "shrink 5s linear forwards",
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                <h5 className="form-date fw-bold mb-0">
                  {formattedSelectedDate}
                </h5>
                <p className="form-subtitle fw-bold mb-3 mt-2">Select</p>

                {["Start Date", "End Date"].map((label, i) => (
                  <div className="mb-3" key={i}>
                    <label className="fw-medium mb-1">
                      {label} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="date"
                        className={`form-control ${
                          validationError &&
                          !newEvent[
                            label === "Start Date" ? "startDate" : "endDate"
                          ]
                            ? "is-invalid"
                            : ""
                        }`}
                        value={
                          newEvent[
                            label === "Start Date" ? "startDate" : "endDate"
                          ]
                        }
                        onChange={(e) =>
                          handleEventInputChange(
                            label === "Start Date" ? "startDate" : "endDate",
                            e.target.value
                          )
                        }
                        required
                      />
                      <span className="input-group-text calendar-icon">
                        <i
                          className="bi bi-calendar-event"
                          style={{ color: "#ff8c00" }}
                        ></i>
                      </span>
                    </div>
                  </div>
                ))}

                {["Start Time", "End Time"].map((label, i) => (
                  <div className="mb-3" key={i}>
                    <label className="fw-medium mb-1">
                      {label} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="time"
                        className={`form-control ${
                          validationError &&
                          !newEvent[
                            label === "Start Time" ? "startTime" : "endTime"
                          ]
                            ? "is-invalid"
                            : ""
                        }`}
                        value={
                          newEvent[
                            label === "Start Time" ? "startTime" : "endTime"
                          ]
                        }
                        onChange={(e) =>
                          handleEventInputChange(
                            label === "Start Time" ? "startTime" : "endTime",
                            e.target.value
                          )
                        }
                        required
                      />
                      <span className="input-group-text time-icon">
                        <i
                          className="bi bi-clock"
                          style={{ color: "#ff8c00" }}
                        ></i>
                      </span>
                    </div>
                  </div>
                ))}

                <div className="mb-3">
                  <label className="fw-medium mb-1">
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      validationError && !newEvent.description
                        ? "is-invalid"
                        : ""
                    }`}
                    rows="2"
                    placeholder="Enter Event Description"
                    value={newEvent.description}
                    onChange={(e) =>
                      handleEventInputChange("description", e.target.value)
                    }
                    required
                  ></textarea>
                </div>

                <div className="form-buttons d-flex gap-2 mt-4">
                  <button
                    className="btn btn-primary save-btn flex-grow-1"
                    onClick={validateAndConfirm}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-secondary cancel-btn flex-grow-1"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Event Form */}
          {showDeleteForm && selectedEvent && !showDeleteConfirmation && (
            <div className="card event-form delete-form m-0 p-3">
              <div className="card-body m-0 p-0">
                <h5 className="form-date m-0 p-0">{formattedSelectedDate}</h5>
                <div className="event-details border rounded-3 mt-3 p-3">
                  <h6 className="event-title m-0 p-0">
                    {selectedEvent.description}
                  </h6>
                  <div
                    className="event-time-range mb-0 p-0"
                    style={{ fontSize: "14px" }}
                  >
                    <strong className="m-0 p-0"></strong>{" "}
                    {selectedEvent.startTime} - {selectedEvent.endTime}
                  </div>
                  <div
                    className="event-date-range"
                    style={{ fontSize: "14px" }}
                  >
                    {formatters.dateForDisplay(
                      formatters.dateFromString(selectedEvent.startDate)
                    )}
                    {selectedEvent.startDate !== selectedEvent.endDate
                      ? ` to ${formatters.dateForDisplay(
                          formatters.dateFromString(selectedEvent.endDate)
                        )}`
                      : ""}
                  </div>
                  <div
                    className="delete-link p-0"
                    style={{ marginTop: "18px" }}
                  >
                    <a
                      href="#"
                      className="text-danger delete-event m-0 p-0"
                      style={{
                        fontSize: "14px",
                        textDecoration: "none",
                        boxShadow: "0px 1px 0px red",
                        display: "inline-block",
                      }}
                      onClick={showDeleteConfirmationDialog}
                    >
                      Delete Event
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Calendar */}
        <div className="col-md-9">
          <div className="card">
            <div className="card-body">
              {/* Calendar header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setCurrentMonth(
                      (prev) =>
                        new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                    )
                  }
                >
                  &lt;
                </button>
                <h4 className="m-0" style={{ color: "#D18237" }}>
                  {formatters.monthYear(currentMonth)}
                </h4>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setCurrentMonth(
                      (prev) =>
                        new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                    )
                  }
                >
                  &gt;
                </button>
              </div>

              {/* Calendar grid */}
              <div className="calendar">
                {/* Weekday headers */}
                <div className="calendar-header">
                  {weekdays.map((day) => (
                    <div key={day} className="calendar-cell weekday">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="calendar-body">
                  {days.map((date, index) => {
                    const dayEvents = getDayEvents(date);
                    const multiDayEvents = getMultiDayEvents(date);
                    return (
                      <div
                        key={index}
                        className={`calendar-cell ${
                          !dateValidators.isCurrentMonth(date)
                            ? "other-month"
                            : ""
                        } 
                          ${dateValidators.isToday(date) ? "today" : ""} 
                          ${dateValidators.isSelected(date) ? "selected" : ""}`}
                        onClick={() => handleDateClick(date)}
                      >
                        <div className="calendar-date">{date.getDate()}</div>

                        {/* Single day events */}
                        {dayEvents.length > 0 && (
                          <div className="event-list">
                            {dayEvents
                              .filter(
                                (event) => event.startDate === event.endDate
                              )
                              .map((event, i) => (
                                <div
                                  key={`single-${i}`}
                                  className="event-item"
                                  onClick={(e) =>
                                    handleEventClick(
                                      date,
                                      event.description,
                                      event,
                                      e
                                    )
                                  }
                                >
                                  {event.description}
                                </div>
                              ))}
                          </div>
                        )}

                        {/* Multi-day events */}
                        {multiDayEvents.length > 0 && (
                          <div className="multi-day-event-list">
                            {multiDayEvents.map((event, i) => {
                              const eventStartDate = formatters.dateFromString(
                                event.startDate
                              );
                              const isStartDate =
                                date.getDate() === eventStartDate.getDate() &&
                                date.getMonth() === eventStartDate.getMonth() &&
                                date.getFullYear() ===
                                  eventStartDate.getFullYear();
                              const isFirstDayOfWeek = date.getDay() === 0;

                              // Determine if this should show a full event or just a continuation line
                              if (isStartDate || isFirstDayOfWeek) {
                                // Calculate how many days to show in current row
                                const daysToEndOfWeek = isStartDate
                                  ? 7 - date.getDay()
                                  : 7;
                                const daysToEndOfEvent =
                                  Math.round(
                                    (formatters.dateFromString(event.endDate) -
                                      date) /
                                      (1000 * 60 * 60 * 24)
                                  ) + 1;
                                const spanDays = Math.min(
                                  daysToEndOfWeek,
                                  daysToEndOfEvent
                                );
                                const eventWidth = `calc(${spanDays * 100}% + ${
                                  (spanDays - 1) * 2
                                }px)`;

                                return (
                                  <div
                                    key={`multi-${i}`}
                                    className="multi-day-event-item"
                                    style={{
                                      width: eventWidth,
                                      position: "absolute",
                                      zIndex: "10",
                                      backgroundColor: "#E4E4E4",
                                      color: "black",
                                      padding: "2px 4px",
                                      fontSize: "10px",
                                      borderRadius: "3px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      height: "20px",
                                      lineHeight: "16px",
                                      marginTop: i * 22 + "px",
                                    }}
                                    onClick={(e) =>
                                      handleEventClick(
                                        date,
                                        event.description,
                                        event,
                                        e
                                      )
                                    }
                                  >
                                    {isFirstDayOfWeek && !isStartDate
                                      ? "→ "
                                      : ""}
                                    {event.description}
                                  </div>
                                );
                              }
                              return null; // Middle day (not start and not first of week)
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Confirmation Dialogs */}
      {(showAddConfirmation || showDeleteConfirmation) && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="confirmation-modal-content">
              <div className="confirmation-header">
                <div className="confirmation-icon">
                  <i className="bi bi-question-circle"></i>
                </div>
                <h5 className="confirmation-title">
                  {showAddConfirmation ? "Continue" : "Delete"}
                </h5>
              </div>
              <p className="confirmation-text">
                {showAddConfirmation
                  ? "Are you sure you want to add this event?"
                  : "Are you sure you want to delete this event?"}
              </p>
              <div className="confirmation-buttons">
                <button
                  className="btn confirmation-confirm"
                  onClick={
                    showAddConfirmation ? handleAddEvent : handleDeleteEvent
                  }
                >
                  Confirm
                </button>
                <button
                  className="btn confirmation-cancel"
                  onClick={handleCancelConfirmation}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .calendar {
          display: flex;
          flex-direction: column;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
        }
        .calendar-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          background-color: #f8f9fa;
          border-bottom: 1px solid #e0e0e0;
        }
        .calendar-body {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          grid-template-rows: repeat(6, minmax(100px, auto));
        }
        .calendar-cell {
          position: relative;
          border: 1px solid #e0e0e0;
          min-height: 100px;
          padding: 5px;
        }
        .calendar-date {
          margin-bottom: 0px;
        }
        .weekday {
          padding: 10px;
          text-align: center;
          font-weight: bold;
        }
        .other-month {
          background-color: #f8f9fa;
          color: #adb5bd;
        }
        .today {
          background-color: #e8f4f8;
        }
        .selected {
          background-color: #e3f2fd;
        }
        .event-item {
          background-color: #d18237;
          color: white;
          padding: 2px 4px;
          margin-bottom: 2px;
          border-radius: 3px;
          font-size: 10px;
          cursor: pointer;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .multi-day-event-list {
          position: relative;
          margin-top: 5px;
          min-height: 20px;
        }
        .event-form {
          margin-top: 1rem;
        }
        .form-buttons {
          margin-top: 1rem;
          gap: 0.5rem;
        }
        .save-btn {
          background-color: #d18237;
          border-color: #d18237;
        }
        .save-btn:hover {
          background-color: #b36f2d;
          border-color: #b36f2d;
        }
        .confirmation-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .confirmation-modal {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          max-width: 450px;
          width: 100%;
        }
        .confirmation-modal-content {
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
        }
        .confirmation-header {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
          width: 100%;
        }
        .confirmation-icon {
          margin-right: 16px;
          font-size: 25px;
          color: #d18237;
          padding: 4px 11px;
          border-radius: 8px;
          background-color: #f5f5f5;
        }
        .confirmation-icon i {
          color: #d18237 !important;
        }
        .confirmation-title {
          font-size: 20px;
          font-weight: 400;
          color: #d18237;
          margin-bottom: 0;
        }
        .confirmation-text {
          color: #666;
          margin-bottom: 24px;
          margin-left: 68px;
          font-size: 14px;
        }
        .confirmation-buttons {
          display: flex;
          gap: 12px;
          width: 100%;
          justify-content: center;
        }
        .confirmation-confirm {
          background-color: #d18237;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 12px 24px;
          font-weight: 500;
          transition: background-color 0.2s;
          min-width: 120px;
        }
        .confirmation-confirm:hover {
          background-color: #d67320;
        }
        .confirmation-cancel {
          background-color: #e8e8e8;
          color: #333;
          border: none;
          border-radius: 4px;
          padding: 12px 24px;
          font-weight: 500;
          transition: background-color 0.2s;
          min-width: 120px;
        }
        .confirmation-cancel:hover {
          background-color: #d8d8d8;
        }
      `}</style>
    </div>
  );
};

Calendar.propTypes = {
  events: PropTypes.object,
  initialDate: PropTypes.string,
  onDateSelect: PropTypes.func,
  onEventSelect: PropTypes.func,
  onEventAdd: PropTypes.func,
  onEventDelete: PropTypes.func,
  onEventsChange: PropTypes.func,
};

export default Calendar;
